import React, { useCallback, useEffect, useState, useRef } from "react";
import Map, { Marker } from "react-map-gl";
import PersonPinIcon from "@mui/icons-material/PersonPin";
import { Box } from "@mui/material";
import InputSearchPlace from "./ModalRunningClub/InputSeachPace";
import { getPlaceForCoordinates } from "../../../../services/rest/mapbox/getPlaces";
import { handleError } from "../../../../utils/handleError";
import toast from "react-hot-toast";

const MapRunningClubProvider = ({
  runningClub,
  setLocation,
  location,
  descriptionLocation,
  setDescriptionLocation,
  setLoading,
}) => {
  const mapRef = useRef();
  const initialCoordinates = runningClub?.location
    ? {
        longitude: runningClub.location[0],
        latitude: runningClub.location[1],
      }
    : {
        longitude: -99.12766,
        latitude: 19.42847,
      };

  const [viewport, setViewport] = useState({
    ...initialCoordinates,
    zoom: 17,
  });

  const [markerPosition, setMarkerPosition] = useState(
    runningClub?.location
      ? [runningClub.location[0], runningClub.location[1]]
      : null
  );

  // Función para centrar el mapa - ahora memoizada correctamente
  const centerMap = useCallback((coordinates) => {
    if (!mapRef.current) return;

    const [longitude, latitude] = coordinates || [
      viewport.longitude,
      viewport.latitude,
    ];

    mapRef.current.flyTo({
      center: [longitude, latitude],
      zoom: 17,
      duration: 1000,
    });

    setMarkerPosition([longitude, latitude]);
    setViewport((prev) => ({
      ...prev,
      longitude,
      latitude,
    }));
  }, []); // Eliminamos dependencias innecesarias

  // Manejador de selección de lugar
  const handlePlaceSelect = useCallback(
    (coordinates, address) => {
      // console.log("Nueva ubicacion", coordinates);
      setLocation(coordinates);
      setMarkerPosition(coordinates);
      setDescriptionLocation(address);
      centerMap(coordinates);
    },
    [setLocation, setDescriptionLocation, centerMap]
  );

  // Efecto inicial para centrar el mapa solo al montar o cuando cambia runningClub.location
  useEffect(() => {
    if (runningClub?.location && !location) {
      centerMap(runningClub.location);
    }
  }, [runningClub?.location]); // Solo dependemos de runningClub.location

  // Manejar clic en el mapa
  const handleMapClick = useCallback(
    async (event) => {
      try {
        const { lngLat } = event;
        const newLocation = [lngLat.lng, lngLat.lat];

        setMarkerPosition([lngLat.lng, lngLat.lat]);

        if (setLocation) {
          setLoading(true);
          // console.log("Nueva ubicacion", newLocation);
          const responsePlace = await getPlaceForCoordinates(newLocation);
          if (!responsePlace) {
            // console.log("Esta undefined", responsePlace);
            setLocation([]);
            setDescriptionLocation("");
            setLoading(false);
            toast.error("No se pudo obtener la ubicación");
          } else {
            // console.log("Respuesta de lugar", responsePlace);
            setLocation([
              responsePlace?.coordinates?.longitude,
              responsePlace?.coordinates?.latitude,
            ]);
            setDescriptionLocation(responsePlace?.full_address ?? "");
            setLoading(false);
          }
        }

        setViewport((prev) => ({
          ...prev,
          longitude: lngLat.lng,
          latitude: lngLat.lat,
        }));
      } catch (error) {
        const { message } = handleError(error);
        // console.log("Error al obtener el lugar", error);
        setLoading(false);
        toast.error(`No se pudo obtener la ubicación. \n ${message}`);
      }
    },
    [setLocation]
  );

  return (
    <Box
      sx={{
        width: 500,
        height: 500,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Box>
        <InputSearchPlace
          searchText={descriptionLocation}
          setSearchText={setDescriptionLocation}
          onPlaceSelect={handlePlaceSelect}
          mapRef={mapRef}
        />
      </Box>
      <Map
        {...viewport}
        ref={mapRef}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        onMove={(evt) => setViewport(evt.viewState)}
        onClick={handleMapClick}
        interactiveLayerIds={["default"]}
      >
        {markerPosition && (
          <Marker longitude={markerPosition[0]} latitude={markerPosition[1]}>
            <PersonPinIcon sx={{ fontSize: 50, color: "black" }} />
          </Marker>
        )}
      </Map>
    </Box>
  );
};

export default MapRunningClubProvider;
